import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchGraphQL } from '../../../lib/graphql';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Text,
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  Badge,
  Stack,
  EmptyState,
  Tooltip
} from '@tonic/shared/components/ui';
import { PageTitle } from "@/components/ui/PageTitle";
import {
  Search,
  Plus,
  Filter,
  Settings,
  AlertCircle,
  Box,
  ArrowUpRight,
  ShieldAlert,
  Zap,
  Brain,
  Clock
} from 'lucide-react';

// Action type icons and colors
const ACTION_TYPES = {
  NOTIFY: {
    icon: Brain,
    label: 'Notify',
    color: 'blue'
  },
  BLOCK: {
    icon: ShieldAlert,
    label: 'Block',
    color: 'red'
  },
  ESCALATE: {
    icon: ArrowUpRight,
    label: 'Escalate',
    color: 'yellow'
  },
  CUSTOM: {
    icon: Settings,
    label: 'Custom',
    color: 'gray'
  }
};

// Helper to format action display
const formatAction = (action) => {
  if (!action) return 'No action';
  return `${action.type}: ${JSON.stringify(action.parameters)}`;
};

// Helper to get action type info
const getActionType = (action) => {
  return ACTION_TYPES[action?.type] || ACTION_TYPES.CUSTOM;
};

const GET_POLICIES = `
  query GetPolicies($filter: PolicyFilter) {
    policies(filter: $filter) {
      id
      name
      description
      status
      rules {
        id
        type
        parameters
        priority
      }
      actions {
        id
        type
        parameters
      }
      metrics {
        totalTriggers
        successRate
        averageResponseTime
        actionSuccessRate
      }
      createdAt
      updatedAt
    }
  }
`;

export function PoliciesList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: null,
    ruleType: null
  });

  const { isLoading: loading, error, data } = useQuery({
    queryKey: ['policies', filters],
    queryFn: () => fetchGraphQL(GET_POLICIES, {
      filter: {
        status: filters.status || undefined,
        ruleType: filters.ruleType || undefined
      }
    }),
    retry: 1
  });

  const handleCreatePolicy = () => {
    navigate('new');
  };

  const handlePolicyClick = (id) => {
    navigate(id);
  };

  const renderPolicyCard = (policy) => {
    const primaryAction = policy.actions[0];
    const actionType = getActionType(primaryAction);
    const ActionIcon = actionType.icon;
    const highestPriorityRule = policy.rules.reduce((max, rule) =>
      !max || rule.priority > max.priority ? rule : max, null);

    return (
      <Card
        key={policy.id}
        onClick={() => handlePolicyClick(policy.id)}
        className="cursor-pointer hover:border-primary transition-colors"
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Text className="font-semibold">{policy.name}</Text>
                <Badge variant={policy.status === 'ACTIVE' ? 'default' : 'secondary'}>
                  {policy.status}
                </Badge>
                {highestPriorityRule?.priority >= 90 && (
                  <Badge variant="warning">
                    <Zap className="h-3 w-3 mr-1" />
                    High Priority
                  </Badge>
                )}
              </div>
              <Text variant="small" color="muted" className="line-clamp-2">{policy.description}</Text>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center gap-4">
              {primaryAction && (
                <div className="flex items-center gap-2">
                  <Tooltip content={actionType.label}>
                    <div className={`p-1 rounded bg-${actionType.color}-100`}>
                      <ActionIcon className={`h-4 w-4 text-${actionType.color}-500`} />
                    </div>
                  </Tooltip>
                  <span>{formatAction(primaryAction)}</span>
                </div>
              )}
              {policy.metrics && (
                <Tooltip content="Success Rate">
                  <Badge variant="outline">
                    {Math.round(policy.metrics.successRate)}% Success
                  </Badge>
                </Tooltip>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <Text variant="small">
                {new Date(policy.updatedAt).toLocaleDateString()}
              </Text>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (error) {
      return (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              icon={AlertCircle}
              title="Error Loading Policies"
              description={error.message || 'An unexpected error occurred. Please try again.'}
              action={
                <Button onClick={() => window.location.reload()}>
                  Retry
                </Button>
              }
            />
          </CardContent>
        </Card>
      );
    }

    if (!data?.policies?.length) {
      return (
        <Card>
          <CardContent className="p-6">
            <EmptyState
              icon={Box}
              title="No Policies Found"
              description={(filters.status || filters.ruleType) ?
                "No policies match your search criteria. Try adjusting your filters." :
                "Create your first AI policy to get started."}
              action={
                <Button onClick={handleCreatePolicy}>
                  <Plus className="h-4 w-4 mr-2" />
                  New Policy
                </Button>
              }
            />
          </CardContent>
        </Card>
      );
    }

    return (
      <Stack space={4}>
        {data.policies.map(renderPolicyCard)}
      </Stack>
    );
  };

  const actions = (
    <Button onClick={handleCreatePolicy}>
      <Plus className="h-4 w-4 mr-2" />
      New Policy
    </Button>
  );

  return (
    <div className="space-y-6">
      <PageTitle
        title="AI Policies"
        description="Manage AI behavior and safety policies"
        actions={actions}
      />

      <Card>
        <CardHeader>
          <CardTitle>Search & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <Stack space={4}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search policies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-4">
              <Select
                value={filters.status || 'all'}
                onValueChange={(value) => setFilters(prev => ({
                  ...prev,
                  status: value === 'all' ? null : value
                }))}
              >
                <SelectTrigger>
                  {filters.status || 'All Status'}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="DISABLED">Disabled</SelectItem>
                  <SelectItem value="TESTING">Testing</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.ruleType || 'all'}
                onValueChange={(value) => setFilters(prev => ({
                  ...prev,
                  ruleType: value === 'all' ? null : value
                }))}
              >
                <SelectTrigger>
                  {filters.ruleType || 'All Rule Types'}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Rule Types</SelectItem>
                  <SelectItem value="CONTENT_MATCH">Content Match</SelectItem>
                  <SelectItem value="USER_BEHAVIOR">User Behavior</SelectItem>
                  <SelectItem value="CONTEXT">Context</SelectItem>
                  <SelectItem value="CUSTOM">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </Stack>
        </CardContent>
      </Card>

      {renderContent()}
    </div>
  );
} 
